import axios from "axios";
import { toast } from 'react-toastify';

const BASE_URL = 'https://aydees-school-backend.aysdevs.com'
// 'http://192.168.1.65:8000';

// Variable globale pour éviter les redirections multiples
let isRedirecting = false;
let hasShownError = false;
let isRefreshing = false;
let failedRefreshAttempts = 0; // Compteur d'échecs de rafraîchissement

// Fonction pour afficher le modal d'authentification
const showAuthModal = (message) => {
  // Import dynamique pour éviter les dépendances circulaires
  import('./global').then(({ default: useGlobale }) => {
    const { showAuthModal } = useGlobale.getState();
    showAuthModal(message);
  }).catch((error) => {
    console.warn('Impossible d\'afficher le modal d\'authentification, redirection vers login');
    redirectToLogin(message);
  });
};

// Fonction de redirection avec délai pour afficher les toasts (utilisée uniquement en dernier recours)
const redirectToLogin = (message) => {
  if (isRedirecting) return;
  if (hasShownError) return;

  isRedirecting = true;
  hasShownError = true;
  
  // Forcer la déconnexion complète avant redirection
  clearAuthDataComplete();
  
  if (message) {
    toast.error(message, {
      onClose: () => {
        window.location.href = '/login';
      }
    });
    
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  } else {
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }
};

// Fonction pour vérifier si un token JWT est expiré
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
});

// Fonction pour rafraîchir le token
const refreshToken = async (refresh) => {
    // Si on a déjà échoué plusieurs fois, ne pas retenter
    if (failedRefreshAttempts >= 2) {
        clearAuthTokensOnly(); // Ne supprimer que les tokens, pas forcer logout
        showAuthModal("Session expirée. Veuillez vous reconnecter pour continuer.");
        return null;
    }

    if (isRefreshing) {
        return new Promise((resolve) => {
            const checkRefreshing = () => {
                if (!isRefreshing) {
                    const tokensString = localStorage.getItem('auth_tokens');
                    if (tokensString) {
                        const tokens = JSON.parse(tokensString);
                        resolve(tokens.access);
                    } else {
                        resolve(null);
                    }
                } else {
                    setTimeout(checkRefreshing, 100);
                }
            };
            checkRefreshing();
        });
    }

    isRefreshing = true;
    
    try {
        if (!refresh || typeof refresh !== 'string') {
            clearAuthTokensOnly();
            return null;
        }
        
        const response = await axios({
            method: 'post',
            url: `${BASE_URL}/api/token/refresh/`,
            data: { refresh: refresh },
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            withCredentials: false,
            timeout: 10000
        });
        
        if (response.data && response.data.access) {
            const tokensString = localStorage.getItem('auth_tokens');
            if (tokensString) {
                try {
                    const tokens = JSON.parse(tokensString);
                    const newTokens = {
                        ...tokens,
                        access: response.data.access
                    };
                    localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
                    failedRefreshAttempts = 0; // Réinitialiser le compteur en cas de succès
                    return response.data.access;
                } catch (parseError) {
                    clearAuthTokensOnly();
                    return null;
                }
            } else {
                return null;
            }
        } else {
            return null;
        }
    } catch (error) {
        failedRefreshAttempts++;
        
        if (error.response?.status === 401) {
            clearAuthTokensOnly(); // Ne pas forcer logout immédiatement
        }
        return null;
    } finally {
        isRefreshing = false;
    }
};

// Fonction pour nettoyer seulement les tokens (sans forcer logout dans le store)
const clearAuthTokensOnly = () => {
  localStorage.removeItem('auth_tokens');
  localStorage.removeItem('credentials');
};

// Fonction pour nettoyer les tokens et forcer la déconnexion complète
const clearAuthDataComplete = () => {
  localStorage.removeItem('auth_tokens');
  localStorage.removeItem('credentials');
  
  // Forcer la déconnexion dans le store global
  try {
    // Import dynamique pour éviter les dépendances circulaires
    import('./global').then(({ default: useGlobale }) => {
      const { forceLogout } = useGlobale.getState();
      forceLogout();
    });
  } catch (error) {
    // Fallback si l'import échoue
    console.warn('Impossible de forcer la déconnexion via le store global');
  }
};

// Fonction pour réinitialiser les variables globales
const resetAuthState = () => {
  isRedirecting = false;
  hasShownError = false;
  isRefreshing = false;
  failedRefreshAttempts = 0;
};

// Intercepteur pour ajouter le token aux requêtes
api.interceptors.request.use(
    async (config) => {
        try {
            // Gérer automatiquement le Content-Type seulement si nécessaire
            if (config.data instanceof FormData) {
                // Pour FormData, laisser axios gérer le Content-Type automatiquement
                delete config.headers['Content-Type'];
            } else if (config.data && typeof config.data === 'object') {
                // Pour les objets JSON, utiliser application/json
                config.headers['Content-Type'] = 'application/json';
            } else if (!config.data) {
                // Pour les requêtes GET sans data, supprimer Content-Type
                delete config.headers['Content-Type'];
            }

            const tokensString = localStorage.getItem('auth_tokens');
            if (tokensString) {
                try {
                    const tokens = JSON.parse(tokensString);
                
                    // Si le token d'accès est expiré, on essaie de le rafraîchir
                    if (!tokens.access || isTokenExpired(tokens.access)) {
                        if (!tokens.refresh) {
                            clearAuthTokensOnly();
                            showAuthModal("Session invalide. Veuillez vous reconnecter pour continuer.");
                            return Promise.reject(new Error('No refresh token'));
                        }
                        
                        const newAccessToken = await refreshToken(tokens.refresh);
                        if (newAccessToken) {
                            config.headers['Authorization'] = `Bearer ${newAccessToken}`;
                        } else {
                            // Si le rafraîchissement échoue, on affiche le modal sans forcer logout
                            clearAuthTokensOnly();
                            showAuthModal("Votre session a expiré. Veuillez vous reconnecter pour continuer votre travail.");
                            return Promise.reject(new Error('Token refresh failed'));
                        }
                    } else {
                        config.headers['Authorization'] = `Bearer ${tokens.access}`;
                    }
                } catch (parseError) {
                    clearAuthTokensOnly();
                    showAuthModal("Données de session corrompues. Veuillez vous reconnecter.");
                    return Promise.reject(new Error('Invalid token data'));
                }
            }
            return config;
        } catch (error) {
            return Promise.reject(error);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
    (response) => {
        if (response.config.responseType === 'blob') {
            return response;
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        if (originalRequest.responseType === 'blob') {
            return Promise.reject(error);
        }
        
        // Vérifier si c'est une erreur d'authentification
        if (error.response?.status === 401) {
            // Si c'est une requête de login, on ne tente pas de rafraîchir le token
            if (originalRequest.url.includes('/utilisateurs/signin/')) {
                return Promise.reject(error);
            }
            
            // Si c'est une requête de rafraîchissement de token, ne pas retenter
            if (originalRequest.url.includes('/api/token/refresh/')) {
                clearAuthTokensOnly(); // Ne pas forcer logout
                showAuthModal("Votre session a expiré. Veuillez vous reconnecter pour continuer.");
                return Promise.reject(error);
            }
            
            // Si la requête n'a pas déjà été retentée et qu'on n'a pas trop échoué
            if (!originalRequest._retry && failedRefreshAttempts < 2) {
                originalRequest._retry = true;
                
                try {
                    const tokensString = localStorage.getItem('auth_tokens');
                    if (tokensString) {
                        const tokens = JSON.parse(tokensString);
                        const newAccessToken = await refreshToken(tokens.refresh);
                        
                        if (newAccessToken) {
                            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                            return api(originalRequest);
                        }
                    }
                } catch (refreshError) {
                    // Ignorer l'erreur de rafraîchissement
                }
                
                // Si le rafraîchissement échoue, on affiche le modal sans forcer logout
                clearAuthTokensOnly();
                showAuthModal("Votre session a expiré pendant que vous travailliez. Reconnectez-vous pour continuer sans perdre vos données.");
            }
        }
        
        return Promise.reject(error);
    }
);

export default api;
export { resetAuthState, clearAuthDataComplete };